import { selectorFamily, useRecoilValue } from 'recoil';
import { isDateInRange } from 'functions';
import { paymentFilter, paymentSummaries, useSetRefreshState } from 'state';
import { PaymentSummary } from 'types';
import { PaymentStatus, HasAdjustments } from 'enums';
import Fuse from 'fuse.js';

export const filteredPayments = selectorFamily<PaymentSummary[], string>({
    key: 'filteredPayments',
    get: (accountId) =>
        async ({ get }) => {
            const filter = get(paymentFilter);
            const {
                dateRange,
                paymentStartAmount,
                paymentEndAmount,
                paymentStatus,
                checkNumber,
                voucherId,
                hasAdjustments,
                adjustmentStartAmount,
                adjustmentEndAmount,
                searchTerm,
                searchKeys,
                sort
            } = filter;

            const payments = get(paymentSummaries({ accountId, dateRange }));

            const list = payments.filter((itemSummary) => {
                const {
                    adjustmentTotalAmount,
                    dotPaymentStatus,
                    paymentAmount,
                    paymentDate,
                    paymentIdRef,
                    voucherNumbers,
                } = itemSummary;

                return isDateInRange(dateRange, paymentDate)
                    && (paymentStartAmount ? paymentAmount >= paymentStartAmount : true)
                    && (paymentEndAmount ? paymentAmount <= paymentEndAmount : true)
                    && (paymentStatus ? filterPaymentStatus(paymentStatus, dotPaymentStatus) : true)
                    && (hasAdjustments ? filterHasAdustments(hasAdjustments, adjustmentTotalAmount) : true)
                    && (adjustmentStartAmount ? adjustmentTotalAmount >= adjustmentStartAmount : true)
                    && (adjustmentEndAmount ? adjustmentTotalAmount <= adjustmentEndAmount : true)
                    && (voucherId ? voucherNumbers?.find((num: string) => num.startsWith(voucherId)) : true)
                    && (checkNumber ? paymentIdRef?.startsWith(checkNumber) : true);
            });

            if(searchTerm && searchTerm !== ""){
                const fuse = new Fuse(list, { keys: searchKeys as string[], threshold: 0.2, ignoreLocation: true });

                return fuse
                        .search(searchTerm)
                        .map((r) => r.item)
                        .sort(sort.compare)
            }

            return list.sort(sort.compare);
        }
});

const filterHasAdustments = (hasAdjustments: HasAdjustments, adjustmentTotalAmount: number) => {
    switch (hasAdjustments) {
        case HasAdjustments.HasAdjustments: return (adjustmentTotalAmount !== 0);
        case HasAdjustments.NoAdjustments: return (adjustmentTotalAmount === 0);
        default: return true;
    }
};

const filterPaymentStatus = (paymentStatus: PaymentStatus, dotPaymentStatus?: string) => {
    switch (paymentStatus) {
        case PaymentStatus.Paid: return (dotPaymentStatus === 'Paid');
        case PaymentStatus.Voided: return (dotPaymentStatus === 'Voided');
        default: return true;
    }
};

export const useFilteredPayments = (accountId: string) => useRecoilValue(filteredPayments(accountId));

export const useRefreshFilteredPayments = (accountId: string) => {
    const { dateRange } = useRecoilValue(paymentFilter);
    return useSetRefreshState(paymentSummaries({ accountId, dateRange }));
};
